import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  FormControlLabel, Switch, Stack
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ComboServices } from "./combos/index.js";
import { useApi } from "../hooks/useApi.js";
import { ui } from "../store/uiStore.js";
import type { Credit, Service } from "@kappa-v2/contracts";

interface Props {
  credit: Credit | null;
  onClose: () => void;
  onSaved: () => void;
}

// Dialog riutilizzabile per modificare una competenza (credito): si SCEGLIE la competenza
// dalla combo (Corsi/extra) → il NOME viene preso dalla combo (non è testo libero); restano
// modificabili descrizione, importo e detraibilità. Usa PATCH /credits/:id → il backend
// ricalcola la scheda.
export function CreditEditDialog({ credit, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { call } = useApi();
  const [serviceId, setServiceId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState(0);
  const [isDeducible, setIsDeducible] = useState(false);
  const [saving, setSaving] = useState(false);
  // Mappa servizi per risolvere id → nome/descrizione quando si sceglie dalla combo.
  const [services, setServices] = useState<Record<string, Service>>({});

  useEffect(() => {
    call<{ data?: Service[] } | Service[]>("/api/v1/services", { silent: true, noLoader: true })
      .then((r) => {
        const list = Array.isArray(r) ? r : (r.data ?? []);
        setServices(Object.fromEntries(list.map((s) => [String(s._id), s])));
      })
      .catch(() => setServices({}));
  }, [call]);

  useEffect(() => {
    if (credit) {
      setServiceId(credit.serviceId ?? "");
      setName(credit.name ?? "");
      setDescription(credit.description ?? "");
      setAmount(credit.credit ?? 0);
      setIsDeducible(Boolean(credit.isDeducible));
    }
  }, [credit]);

  // Quando si sceglie un servizio dalla combo, si aggiornano TUTTI i campi da quel servizio
  // (nome, descrizione, importo, detraibile) — poi restano modificabili a mano.
  const onSelectService = (id: string) => {
    setServiceId(id);
    const s = services[id];
    if (s) {
      setName(s.name ?? "");
      setDescription(s.description ?? "");
      setAmount(s.price ?? 0);
      setIsDeducible(Boolean(s.isDeducible));
    }
  };

  const save = async () => {
    if (!credit?._id) return;
    setSaving(true);
    try {
      await call(`/api/v1/payments/credits/${credit._id}`, {
        method: "PATCH",
        body: { serviceId, name, description, credit: Number(amount) || 0, isDeducible }
      });
      ui.toast(t("common.saved"), "success");
      onSaved();
      onClose();
    } catch {
      ui.toast(t("common.error"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(credit)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("credits.editTitle")}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ComboServices label={t("credits.competenceLabel")} value={serviceId} onChange={onSelectService} />
          <TextField fullWidth size="small" label={t("common.description")} value={description} onChange={(e) => setDescription(e.target.value)} multiline minRows={2} />
          <TextField fullWidth size="small" type="number" label={`${t("common.amount")} (€)`} value={amount}
            onChange={(e) => setAmount(e.target.value === "" ? 0 : Number(e.target.value))} inputProps={{ step: "0.01" }} />
          <FormControlLabel control={<Switch checked={isDeducible} onChange={(e) => setIsDeducible(e.target.checked)} />} label={t("credits.deductible")} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
        <Button variant="contained" onClick={save} disabled={saving || !name.trim()}>{t("common.save")}</Button>
      </DialogActions>
    </Dialog>
  );
}
